/**
 * ClientAIWorkoutGenerator.jsx — self-service : le client répond au même questionnaire que
 * son coach (objectif, jours/semaine, niveau, matériel, durée...), l'IA (Claude Sonnet 4.6)
 * génère un programme complet. Modifiable (régénération séance par séance, aléatoire ou avec
 * instructions précises) jusqu'à validation définitive — ensuite verrouillé. 1 crédit inclus/mois
 * (Communauté) ou 5 (Transformation/VIP), puis 5 CHF/génération supplémentaire via Stripe.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bot, Sparkles, Loader2, Wand2, RefreshCw, Check, ImageOff, Shuffle, MessageSquarePlus,
  ChevronLeft, CreditCard, CalendarDays, History, CheckCircle2, Clock3, XCircle,
  Library, BrainCircuit, Coins,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { ProgramWizard } from './ProgramWizard';
import { PackModal, CreditsBar } from './CustomExerciseLibrary';

const PENDING_JOB_KEY = 'bodyup_ai_pending_job';
const GOAL_LABELS = { weight_loss: 'Perte de gras', muscle_gain: 'Prise de muscle', maintenance: 'Maintien', recomposition: 'Recomposition corporelle' };
const LEVEL_LABELS = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };

const estimateSessionMinutes = (session) => {
  const seconds = 180 + (session.exercises || []).reduce(
    (sum, ex) => sum + (ex.sets_count || 0) * (35 + (ex.rest_seconds || 60)), 0,
  );
  return Math.round(seconds / 60);
};

const WEEKDAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEK_PATTERNS = {
  1: [0], 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4], 5: [0, 1, 2, 3, 4], 6: [0, 1, 2, 3, 4, 5], 7: [0, 1, 2, 3, 4, 5, 6],
};
const buildWeekSchedule = (sessionsCount) => {
  const pattern = WEEK_PATTERNS[sessionsCount] || Array.from({ length: Math.min(sessionsCount, 7) }, (_, i) => i);
  const dayToSession = {};
  pattern.forEach((dayIdx, sessionIdx) => { dayToSession[dayIdx] = sessionIdx; });
  return dayToSession;
};

export const ClientAIWorkoutGenerator = ({ en, onBack, onValidated, onOpenProgram }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [credits, setCredits] = useState(null);
  const [exCredits, setExCredits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [regenIdx, setRegenIdx] = useState(null);
  const [regenPanelIdx, setRegenPanelIdx] = useState(null);
  const [regenFeedbackText, setRegenFeedbackText] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [validating, setValidating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [wizardInitial, setWizardInitial] = useState({});
  const [pendingWizardData, setPendingWizardData] = useState(null);
  const [needsCredits, setNeedsCredits] = useState(null); // job complet en statut 'needs_credits'
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [busyPack, setBusyPack] = useState(null);
  const [resolving, setResolving] = useState(false);

  const loadCredits = useCallback(() => {
    authFetch('/api/tracking/ai-workout/credits').then((r) => r.json()).then(setCredits).catch(() => {});
  }, [authFetch]);
  useEffect(() => { loadCredits(); }, [loadCredits]);

  const loadExCredits = useCallback(() => {
    authFetch('/api/tracking/custom-exercises/credits/state').then((r) => r.json()).then(setExCredits).catch(() => {});
  }, [authFetch]);
  useEffect(() => { loadExCredits(); }, [loadExCredits]);

  // Préremplit depuis le propre profil vivant du client — pas besoin de tout retaper.
  useEffect(() => {
    authFetch('/api/tracking/profile').then((r) => r.json()).then((profile) => {
      if (!profile || !profile.goal) return;
      setWizardInitial((f) => ({
        ...f,
        goal: f.goal || GOAL_LABELS[profile.goal] || f.goal,
        daysPerWeek: profile.training_frequency || f.daysPerWeek,
        level: f.level || LEVEL_LABELS[profile.training_level] || f.level,
        sessionDurationMinutes: f.sessionDurationMinutes || (profile.training_duration ? String(profile.training_duration) : f.sessionDurationMinutes),
      }));
    }).catch(() => {});
  }, []);

  // Retour de Stripe (achat crédit supplémentaire) → confirmer le paiement puis créditer.
  useEffect(() => {
    const sid = searchParams.get('ai_credit_session');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    let stop = false;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await authFetch(`/api/tracking/ai-workout/credits/checkout-status/${sid}`);
        const d = await r.json();
        if (d.payment_status === 'paid') {
          searchParams.delete('ai_credit_session');
          setSearchParams(searchParams, { replace: true });
          setCredits(d); setPolling(false);
          toast.success(L('Crédit ajouté !', 'Credit added!'));
          return;
        }
      } catch { /* retry */ }
      if (!stop && attempts < 6) setTimeout(tick, 2000); else setPolling(false);
    };
    tick();
    return () => { stop = true; };
  }, []);

  // Retour de Stripe (achat de crédits "bibliothèque perso", pool PARTAGÉ avec les nouveaux
  // exercices IA) → confirme le paiement puis relance automatiquement le job en attente.
  useEffect(() => {
    const sid = searchParams.get('custom_ex_credit_session');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    let stop = false;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await authFetch(`/api/tracking/custom-exercises/credits/checkout-status/${sid}`);
        const d = await r.json();
        if (d.payment_status === 'paid') {
          searchParams.delete('custom_ex_credit_session');
          setSearchParams(searchParams, { replace: true });
          setExCredits(d); setPolling(false);
          toast.success(L('Crédits ajoutés !', 'Credits added!'));
          const pendingJobId = localStorage.getItem(PENDING_JOB_KEY);
          if (pendingJobId) { localStorage.removeItem(PENDING_JOB_KEY); resolveNewExercises(pendingJobId, false); }
          return;
        }
      } catch { /* retry */ }
      if (!stop && attempts < 6) setTimeout(tick, 2000); else setPolling(false);
    };
    tick();
    return () => { stop = true; };
  }, []);

  const hasCredit = !!credits && (credits.ai_included_remaining > 0 || credits.ai_purchased > 0);

  const generate = (wizardData, allowNewExercises) => {
    if (!hasCredit) return;
    setBusy(true);
    const body = {
      goal: wizardData.goal, location: wizardData.location, days_per_week: +wizardData.daysPerWeek, level: wizardData.level || null,
      equipment: wizardData.equipment, equipment_tags: wizardData.equipmentTags,
      duration_weeks: +wizardData.durationWeeks,
      session_duration_minutes: wizardData.sessionDurationMinutes ? +wizardData.sessionDurationMinutes : null,
      extra_notes: wizardData.extraNotes,
      allow_new_exercises: !!allowNewExercises,
    };
    authFetch('/api/tracking/ai-workout/generate', { method: 'POST', body: JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then((d) => poll(d.job_id))
      .catch((j) => {
        toast.error(j?.detail === 'no_ai_credit' ? L('Aucun crédit disponible.', 'No credit available.') : L("La génération IA a échoué. Réessayez.", 'AI generation failed. Try again.'));
        setBusy(false); loadCredits();
      });
  };

  const poll = (jobId, attempt = 0) => {
    if (attempt >= 60) { setBusy(false); setResolving(false); toast.error(L('La génération prend trop de temps. Réessayez plus tard.', 'Generation is taking too long. Try again later.')); return; }
    authFetch(`/api/tracking/ai-workout/generate/${jobId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((job) => {
        if (job.status === 'generating') { setTimeout(() => poll(jobId, attempt + 1), 3000); return; }
        setBusy(false); setResolving(false);
        loadCredits(); loadExCredits();
        if (job.status === 'ready') { setNeedsCredits(null); setPreview({ job_id: jobId, program: job.program, constraints: job.constraints }); }
        else if (job.status === 'needs_credits') { setPreview(null); setNeedsCredits({ job_id: jobId, ...job }); }
        else toast.error(job.error || L('La génération IA a échoué. Réessayez.', 'AI generation failed. Try again.'));
      })
      .catch(() => { setBusy(false); setResolving(false); toast.error(L('Connexion perdue pendant la génération. Réessayez.', 'Connection lost during generation. Try again.')); });
  };

  const resolveNewExercises = (jobId, useLibraryOnly) => {
    setResolving(true);
    authFetch(`/api/tracking/ai-workout/generate/${jobId}/resolve-new-exercises`, {
      method: 'POST', body: JSON.stringify({ use_library_only: useLibraryOnly }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => poll(jobId))
      .catch(() => { setResolving(false); toast.error(L('Échec de la reprise. Réessayez.', 'Failed to resume. Try again.')); });
  };

  const buyExerciseCredits = (pack) => {
    if (needsCredits) localStorage.setItem(PENDING_JOB_KEY, needsCredits.job_id);
    setBusyPack(pack);
    authFetch('/api/tracking/custom-exercises/credits/checkout', {
      method: 'POST', body: JSON.stringify({ pack, origin_url: window.location.origin }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (d.url) window.location.href = d.url; })
      .catch(() => { setBusyPack(null); toast.error(L('Erreur de paiement.', 'Payment error.')); });
  };

  const regenerateSession = (idx, feedback) => {
    setRegenIdx(idx); setRegenPanelIdx(null);
    authFetch(`/api/tracking/ai-workout/generate/${preview.job_id}/regenerate-session`, {
      method: 'POST', body: JSON.stringify({ session_index: idx, feedback: feedback || undefined }),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then((d) => {
        setPreview((p) => ({ ...p, program: d.program }));
        setRegenFeedbackText('');
        toast.success(L('Séance régénérée !', 'Session regenerated!'));
      })
      .catch((j) => toast.error(j?.detail || L('Échec de la régénération.', 'Regeneration failed.')))
      .finally(() => setRegenIdx(null));
  };

  const validate = () => {
    setValidating(true);
    authFetch(`/api/tracking/ai-workout/generate/${preview.job_id}/validate`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { toast.success(L('Programme validé et assigné !', 'Program validated and assigned!')); onValidated(); })
      .catch(() => toast.error(L('Échec de la validation.', 'Validation failed.')))
      .finally(() => setValidating(false));
  };

  const payForCredit = () => {
    setPayBusy(true);
    authFetch('/api/tracking/ai-workout/credits/checkout', { method: 'POST', body: JSON.stringify({ origin_url: window.location.origin }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (d.url) window.location.href = d.url; })
      .catch(() => { setPayBusy(false); toast.error(L('Paiement impossible.', 'Payment failed.')); });
  };

  const openHistory = () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    authFetch('/api/tracking/ai-workout/history').then((r) => r.json()).then((d) => setHistory(d.history || []))
      .catch(() => setHistory([])).finally(() => setHistoryLoading(false));
  };
  const resumePreview = (job) => {
    setPreview({ job_id: job.job_id, program: job.program, constraints: job.constraints });
    setHistoryOpen(false);
  };
  const resumeNeedsCredits = (job) => {
    setNeedsCredits({ job_id: job.job_id, ...job });
    setHistoryOpen(false);
  };
  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(en ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const WEEKDAYS = en ? WEEKDAYS_EN : WEEKDAYS_FR;

  if (polling) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="ai-credit-polling">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        <p className="text-zinc-300 text-sm font-semibold">{L('Vérification de votre paiement…', 'Verifying your payment…')}</p>
      </div>
    );
  }

  if (resolving) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="ai-resolving-screen">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        <p className="text-zinc-300 text-sm font-semibold">{L('Reprise de votre génération…', 'Resuming your generation…')}</p>
        <p className="text-zinc-500 text-xs">{L("Création des nouveaux exercices IA en cours, cela peut prendre jusqu'à 60 secondes.", 'Creating the new AI exercises, this can take up to 60 seconds.')}</p>
      </div>
    );
  }

  if (historyOpen) {
    return (
      <div className="space-y-4" data-testid="client-ai-history-screen">
        <button onClick={() => setHistoryOpen(false)} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="ai-history-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <h2 className="text-lg font-black text-white flex items-center gap-2"><History className="w-5 h-5 text-blue-400" /> {L('Historique de mes générations IA', 'My AI generation history')}</h2>

        {historyLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-zinc-500 py-8 text-center" data-testid="ai-history-empty">{L('Aucune génération pour le moment.', 'No generation yet.')}</p>
        ) : (
          <div className="space-y-2.5">
            {history.map((job) => {
              const name = job.program?.name || job.constraints?.goal || L('Génération', 'Generation');
              const goal = job.program?.goal || job.constraints?.goal || '';
              return (
                <div key={job.job_id} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3.5" data-testid={`ai-history-item-${job.job_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{name}</p>
                      <p className="text-xs text-zinc-500 truncate">{goal}</p>
                      <p className="text-[11px] text-zinc-600 mt-0.5">{formatDate(job.created_at)}</p>
                    </div>
                    <div className="shrink-0">
                      {job.validated ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" data-testid={`ai-history-status-validated-${job.job_id}`}><CheckCircle2 className="w-3 h-3" /> {L('Validé', 'Validated')}</span>
                      ) : job.status === 'error' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-300 border border-red-500/25" data-testid={`ai-history-status-error-${job.job_id}`}><XCircle className="w-3 h-3" /> {L('Échec', 'Failed')}</span>
                      ) : job.status === 'generating' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/15 text-blue-300 border border-blue-500/25" data-testid={`ai-history-status-generating-${job.job_id}`}><Loader2 className="w-3 h-3 animate-spin" /> {L('En cours', 'In progress')}</span>
                      ) : job.status === 'needs_credits' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/15 text-orange-300 border border-orange-500/25" data-testid={`ai-history-status-needs-credits-${job.job_id}`}><Coins className="w-3 h-3" /> {L('Crédits requis', 'Credits needed')}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/25" data-testid={`ai-history-status-unfinished-${job.job_id}`}><Clock3 className="w-3 h-3" /> {L('Non finalisé', 'Not finalized')}</span>
                      )}
                    </div>
                  </div>
                  {job.status === 'error' && job.error && <p className="text-[11px] text-red-400/80 mt-1.5">{job.error}</p>}
                  <div className="mt-2.5 flex items-center gap-3">
                    {job.validated && job.program_id && onOpenProgram && (
                      <button onClick={() => onOpenProgram(job.program_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-300 hover:text-blue-200" data-testid={`ai-history-open-btn-${job.job_id}`}>
                        {L('Voir mon programme', 'View my program')}
                      </button>
                    )}
                    {!job.validated && job.status === 'ready' && job.program && (
                      <button onClick={() => resumePreview(job)} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-300 hover:text-white" data-testid={`ai-history-resume-btn-${job.job_id}`}>
                        {L("Reprendre l'aperçu", 'Resume preview')}
                      </button>
                    )}
                    {job.status === 'needs_credits' && (
                      <button onClick={() => resumeNeedsCredits(job)} className="inline-flex items-center gap-1 text-xs font-semibold text-orange-300 hover:text-orange-200" data-testid={`ai-history-resume-credits-btn-${job.job_id}`}>
                        {L('Compléter avec des crédits', 'Complete with credits')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (needsCredits) {
    const nc = needsCredits;
    return (
      <div className="space-y-4" data-testid="client-ai-needs-credits-screen">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-white flex items-center gap-2"><Coins className="w-5 h-5 text-orange-400" /> {L('Crédits nécessaires', 'Credits needed')}</h2>
          <CreditsBar en={en} credits={exCredits} onBuy={() => setPackModalOpen(true)} buying={!!busyPack} />
        </div>
        <div className="rounded-xl bg-orange-500/10 border border-orange-500/25 p-4 space-y-2" data-testid="ai-needs-credits-banner">
          <p className="text-sm text-orange-100">
            {L(
              `Sur les ${nc.total_exercise_slots} exercices de ce programme, ${nc.library_exercise_slots} viennent de votre bibliothèque existante et ${nc.invented_exercise_slots} nécessitent la création de ${nc.credits_needed} nouvel(aux) exercice(s) IA — soit ${nc.credits_needed} crédit(s) (pool "Ma bibliothèque créée", 1 crédit = 1 exercice storyboard).`,
              `Out of ${nc.total_exercise_slots} exercises in this program, ${nc.library_exercise_slots} come from your existing library and ${nc.invented_exercise_slots} require creating ${nc.credits_needed} new AI exercise(s) — that's ${nc.credits_needed} credit(s) ("My created library" pool, 1 credit = 1 storyboard exercise).`
            )}
          </p>
          <p className="text-xs text-orange-300/80">
            {L(`Vous avez actuellement ${exCredits?.balance ?? nc.credits_available} crédit(s) disponible(s). 5 crédits inclus chaque mois avec votre abonnement.`, `You currently have ${exCredits?.balance ?? nc.credits_available} credit(s) available. 5 credits included every month with your subscription.`)}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setPackModalOpen(true)} disabled={resolving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="ai-needs-credits-buy-btn">
            <CreditCard className="w-4 h-4" /> {L('Acheter des crédits', 'Buy credits')}
          </button>
          <button onClick={() => resolveNewExercises(nc.job_id, true)} disabled={resolving}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors disabled:opacity-60"
            data-testid="ai-needs-credits-use-library-btn">
            {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Library className="w-4 h-4" />} {L('Utiliser ma bibliothèque existante', 'Use my existing library')}
          </button>
        </div>
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="ai-needs-credits-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <PackModal en={en} open={packModalOpen} onClose={() => setPackModalOpen(false)} onPick={buyExerciseCredits} packs={exCredits?.packs} busyPack={busyPack} />
      </div>
    );
  }

  if (preview) {
    return (
      <div className="space-y-4" data-testid="client-ai-preview-screen">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-400" /> {L('Aperçu avant validation', 'Preview before validation')}</h2>
        </div>
        <p className="text-white font-semibold">{preview.program.name}</p>
        <p className="text-xs text-zinc-400 mb-1">{preview.program.goal}</p>

        <div className="p-3 bg-zinc-800/40 border border-white/10 rounded-xl" data-testid="ai-preview-week-calendar">
          <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {L('Répartition indicative de la semaine', 'Indicative week schedule')}</p>
          <div className="grid grid-cols-7 gap-1.5">
            {WEEKDAYS.map((day, i) => {
              const sessionIdx = buildWeekSchedule(preview.program.sessions.length)[i];
              const hasSession = sessionIdx !== undefined;
              return (
                <div key={day} className={`rounded-lg py-2 text-center ${hasSession ? 'bg-blue-500/20 border border-blue-500/40' : 'bg-zinc-900/60 border border-white/5'}`} data-testid={`ai-preview-calendar-day-${i}`}>
                  <p className="text-[10px] text-zinc-500">{day}</p>
                  {hasSession ? <p className="text-[10px] text-blue-300 font-bold mt-0.5">S{sessionIdx + 1}</p> : <p className="text-[10px] text-zinc-500 mt-0.5">{L('Repos', 'Rest')}</p>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          {preview.program.sessions.map((s, i) => {
            const minutes = estimateSessionMinutes(s);
            return (
              <div key={i} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3" data-testid={`client-ai-preview-session-${i}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-white font-semibold text-sm">{s.session_label}</p>
                    <p className="text-xs text-zinc-500">~{minutes} {L('min estimées', 'min estimated')} · {s.exercises.length} {L('exercice(s)', 'exercise(s)')}</p>
                  </div>
                  <button onClick={() => { setRegenPanelIdx(regenPanelIdx === i ? null : i); setRegenFeedbackText(''); }} disabled={regenIdx === i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-blue-500/30 text-blue-300 text-xs font-semibold rounded-lg hover:bg-blue-500/10 transition-colors disabled:opacity-50"
                    data-testid={`client-ai-regenerate-btn-${i}`}>
                    {regenIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {L('Régénérer', 'Regenerate')}
                  </button>
                </div>

                {regenPanelIdx === i && (
                  <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-2" data-testid={`client-ai-regenerate-panel-${i}`}>
                    <button onClick={() => regenerateSession(i, null)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold rounded-lg hover:bg-white/10 transition-colors"
                      data-testid={`client-ai-regenerate-random-btn-${i}`}>
                      <Shuffle className="w-3.5 h-3.5" /> {L('Aléatoire (surprends-moi)', 'Random (surprise me)')}
                    </button>
                    <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 font-semibold pt-1">
                      <MessageSquarePlus className="w-3.5 h-3.5" /> {L('Ou précisez ce que vous voulez changer :', 'Or specify what you want to change:')}
                    </div>
                    <textarea
                      value={regenFeedbackText} onChange={(e) => setRegenFeedbackText(e.target.value)} rows={2}
                      placeholder={L("Ex: moins d'exercices d'épaules, plus axé jambes...", 'Ex: fewer shoulder exercises, more leg-focused...')}
                      className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder:text-zinc-600 focus:border-blue-500/50 focus:outline-none resize-none"
                      data-testid={`client-ai-regenerate-feedback-input-${i}`}
                    />
                    <button onClick={() => regenerateSession(i, regenFeedbackText)} disabled={!regenFeedbackText.trim()}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-blue-600 to-cyan-400 text-white text-xs font-bold rounded-lg hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:hover:scale-100"
                      data-testid={`client-ai-regenerate-with-feedback-btn-${i}`}>
                      <RefreshCw className="w-3.5 h-3.5" /> {L('Régénérer avec ces instructions', 'Regenerate with these instructions')}
                    </button>
                  </div>
                )}

                <ul className="mt-2 space-y-1">
                  {s.exercises.map((ex) => (
                    <li key={ex.exercise_id || ex.name} className="text-xs text-zinc-400 flex items-center gap-2">
                      {ex.image_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy" width={28} height={28} className="w-7 h-7 rounded object-contain border border-white/10 flex-shrink-0 bg-zinc-900" />
                      ) : (
                        <ImageOff className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      )}
                      <span className="text-zinc-300">{ex.name}</span>
                      <span className="text-zinc-600">· {ex.sets_count}×{ex.reps_scheme} · {L('repos', 'rest')} {ex.rest_seconds}s</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button onClick={validate} disabled={validating}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-400 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="client-ai-validate-btn">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Valider définitivement', 'Validate definitively')}
          </button>
          <button onClick={onBack} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid="client-ai-cancel-btn">
            {L('Annuler', 'Cancel')}
          </button>
        </div>
        <p className="text-[11px] text-zinc-500">{L('Une fois validé, le programme est verrouillé (non modifiable) — vous pourrez toujours demander un ajustement à votre coach.', 'Once validated, the program is locked — you can always ask your coach for adjustments.')}</p>
      </div>
    );
  }

  if (pendingWizardData) {
    return (
      <div className="space-y-5" data-testid="client-ai-choice-screen">
        <h2 className="text-lg font-black text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-400" /> {L('Un dernier choix avant de générer', 'One last choice before generating')}</h2>
        <p className="text-sm text-zinc-400">{L("D'où l'IA doit-elle piocher les exercices de votre programme ?", "Where should the AI pick your program's exercises from?")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={() => { const d = pendingWizardData; setPendingWizardData(null); generate(d, false); }}
            className="text-left p-4 rounded-2xl bg-zinc-800/60 border border-white/10 hover:border-blue-500/40 transition-colors"
            data-testid="ai-choice-library-only-btn">
            <Library className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-white font-bold text-sm mb-1">{L('Bibliothèque existante', 'Existing library')}</p>
            <p className="text-xs text-zinc-500">{L("Rapide et gratuit. L'IA choisit uniquement parmi les exercices Body-Up déjà illustrés.", 'Fast and free. The AI only picks from Body-Up exercises already illustrated.')}</p>
          </button>
          <button onClick={() => { const d = pendingWizardData; setPendingWizardData(null); generate(d, true); }}
            className="text-left p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-400/10 border border-blue-500/30 hover:border-blue-400/60 transition-colors"
            data-testid="ai-choice-allow-new-btn">
            <BrainCircuit className="w-6 h-6 text-cyan-300 mb-2" />
            <p className="text-white font-bold text-sm mb-1">{L('Autoriser de nouveaux exercices IA', 'Allow new AI exercises')}</p>
            <p className="text-xs text-zinc-500">{L("Si aucun exercice existant ne convient parfaitement (matériel/objectif précis), l'IA en crée un nouveau (image storyboard). ", 'If no existing exercise fits perfectly (specific equipment/goal), the AI creates a new one (storyboard image). ')}
              <span className="text-cyan-300 font-semibold">{L('1 crédit "Ma bibliothèque créée" par nouvel exercice', '1 "My created library" credit per new exercise')}</span>
              {L(' (5 inclus/mois).', ' (5 included/month).')}
            </p>
          </button>
        </div>
        <button onClick={() => setPendingWizardData(null)} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="ai-choice-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="client-ai-generator">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="client-ai-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-black text-white flex items-center gap-2"><Bot className="w-5 h-5 text-blue-400" /> {L("Générer avec l'IA", 'Generate with AI')}</h2>
        <button onClick={openHistory} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-300 text-xs font-semibold hover:border-blue-500/40 hover:text-blue-300 transition-colors" data-testid="ai-history-btn">
          <History className="w-3.5 h-3.5" /> {L('Historique', 'History')}
        </button>
      </div>

      {credits && (
        <div className="rounded-xl bg-blue-500/10 border border-blue-500/25 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="client-ai-credit-banner">
          <p className="text-sm text-blue-200">
            {hasCredit
              ? L(`Crédit disponible : ${credits.ai_included_remaining} inclus${credits.ai_purchased ? ` + ${credits.ai_purchased} acheté(s)` : ''} — cette génération sera gratuite.`, `Credit available: ${credits.ai_included_remaining} included${credits.ai_purchased ? ` + ${credits.ai_purchased} purchased` : ''} — this generation will be free.`)
              : L(`Vous avez utilisé vos crédits gratuits de ce mois (${credits.ai_per_payment}/mois). Une nouvelle génération coûte ${credits.extra_price_chf} CHF.`, `You've used your free credits this month (${credits.ai_per_payment}/month). A new generation costs ${credits.extra_price_chf} CHF.`)}
          </p>
          {!hasCredit && (
            <button onClick={payForCredit} disabled={payBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-cyan-400 hover:scale-105 transition-transform disabled:opacity-50 whitespace-nowrap"
              data-testid="client-ai-pay-btn">
              {payBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />} {L(`Payer ${credits.extra_price_chf} CHF`, `Pay ${credits.extra_price_chf} CHF`)}
            </button>
          )}
        </div>
      )}

      {busy ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3" data-testid="client-ai-generating">
          <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
          <p className="text-zinc-300 text-sm font-semibold">{L("L'IA construit votre programme...", 'AI is building your program...')}</p>
          <p className="text-zinc-500 text-xs">{L("Cela peut prendre jusqu'à 60 secondes.", 'This can take up to 60 seconds.')}</p>
        </div>
      ) : !hasCredit ? (
        <div className="text-center py-8 text-sm text-zinc-500" data-testid="client-ai-no-credit-block">
          {L('Achetez un crédit ci-dessus pour lancer le questionnaire.', 'Buy a credit above to start the questionnaire.')}
        </div>
      ) : (
        <ProgramWizard en={en} mode="ai" initial={wizardInitial} onCancel={onBack} onComplete={setPendingWizardData} />
      )}
    </div>
  );
};

export default ClientAIWorkoutGenerator;
